import {Alert, List, Select, Tag} from 'antd';
import moment from 'moment';
import {formatEnumLabel, personName} from 'pages/kybDashboard/kybUtils';
import {KYC_HUB_ACTIVITY} from './resolvers';
import {
  HUB_PAGE_SIZE,
  activityDetailEntries,
  riskActivityDetails,
} from './kycHubUtils';
import {useHubList} from './useHubList';
import styles from './kycHub.module.scss';

// Actions seen on daba-dev. The filter also offers any action on the current
// page, so one added later shows up once it has happened.
const KNOWN_ACTIONS = [
  'DOCUMENT_ADDED',
  'DOCUMENT_REPLACED',
  'DOCUMENT_REVIEWED',
  'DOCUMENT_DELETED',
  'NOTE_ADDED',
  'NOTE_UPDATED',
  'NOTE_DELETED',
  'COMPLIANCE_RISK_UPDATED',
  'COMPLIANCE_RISK_CLEARED',
];

// Codes such as PROOF_ADDRESS read as words; free text and ids stay as sent.
const ENUM_VALUE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$|^[A-Z]{3,}$/;
const OBJECT_ID = /^[a-f\d]{24}$/i;

const dotClass = action => {
  if (/DELETED/.test(action)) return styles.eventDotDeleted;
  if (/^NOTE_/.test(action)) return styles.eventDotNote;
  if (/^COMPLIANCE_RISK_/.test(action)) return styles.eventDotRisk;
  if (/^DOCUMENT_/.test(action)) return styles.eventDotDocument;
  return styles.eventDot;
};

const DetailValue = ({value}) => {
  if (OBJECT_ID.test(value)) {
    return <span className={styles.eventId}>{value}</span>;
  }
  return ENUM_VALUE.test(value) ? formatEnumLabel(value) : value;
};

/**
 * The subject's append-only timeline, newest first.
 *
 * Every action renders, including ones added after this screen was written:
 * the action string is humanised and `details` is shown key by key without
 * assuming any particular shape. System and provider events have no actor.
 */
export const HubActivity = ({subject}) => {
  const list = useHubList(KYC_HUB_ACTIVITY, 'kycHubActivity', subject);
  const actionOptions = [
    ...KNOWN_ACTIONS,
    ...list.rows.map(row => row.action),
    list.filters.action,
  ]
    .filter((action, index, all) => action && all.indexOf(action) === index)
    .map(action => ({value: action, label: formatEnumLabel(action)}));

  return (
    <div>
      <div className={styles.filterBar}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          className={styles.actionFilter}
          placeholder="All activity"
          value={list.filters.action}
          options={actionOptions}
          onChange={action => list.setFilter({action})}
        />
      </div>

      {list.error && (
        <Alert
          type="error"
          showIcon
          className={styles.alert}
          message={list.error}
        />
      )}

      <List
        className={styles.eventList}
        loading={list.loading}
        dataSource={list.rows}
        split={false}
        locale={{
          emptyText: list.filters.action
            ? 'No activity of this kind.'
            : 'No activity recorded.',
        }}
        // One page needs no pager.
        pagination={
          list.total > HUB_PAGE_SIZE && {
            current: list.page,
            pageSize: HUB_PAGE_SIZE,
            total: list.total,
            onChange: list.setPage,
            showSizeChanger: false,
            showTotal: total => `${total} event${total === 1 ? '' : 's'}`,
          }
        }
        renderItem={event => {
          const details = activityDetailEntries(
            /^COMPLIANCE_RISK_/.test(event.action)
              ? riskActivityDetails(event.details)
              : event.details,
          );
          return (
            <List.Item className={styles.eventItem}>
              <span className={dotClass(event.action)} aria-hidden />
              <div className={styles.eventBody}>
                <div className={styles.eventHead}>
                  <strong>{formatEnumLabel(event.action)}</strong>
                  <span className={styles.subtle}>
                    {moment(event.createdAt).format('DD MMM YYYY, HH:mm')}
                  </span>
                </div>
                <div className={styles.eventActor}>
                  {/* System and provider events have no person behind them. */}
                  by{' '}
                  {event.actor
                    ? personName(event.actor)
                    : formatEnumLabel(event.actorType)}
                  {event.actor && event.actorType && (
                    <Tag>{formatEnumLabel(event.actorType)}</Tag>
                  )}
                </div>
                {details.length > 0 && (
                  <dl className={styles.eventDetails}>
                    {details.map(([label, value]) => (
                      <div key={label} className={styles.eventDetail}>
                        <dt>{label}</dt>
                        <dd>
                          <DetailValue value={value} />
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </List.Item>
          );
        }}
      />
    </div>
  );
};
